import React, { useState, useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, SelectProps, TextField, Checkbox, FormControlLabel, Box, Chip, CircularProgress, Autocomplete, AutocompleteRenderInputParams, Button, Typography, Paper, IconButton } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CancelIcon from "@mui/icons-material/Cancel";
import { FieldAttributes, Form, Formik, useField, useFormikContext } from 'formik';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { StyledDropzone } from '../Elements/Dropzone';
import type { PartnerModel, StatesModel } from '../../Pages/RequestSignature';
import * as yup from 'yup';
import { FMTextField } from '../Elements/input';
import { GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../../helper/theme';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { RadioGroup } from '@mui/material';

export interface PartnerLicenseeRow {
    partnerLincenseeInformationId: number;
    licensee: string;
    licenseState: string;
    stateId: number;
    stateName: string;
}

export interface AddRequestSignatureModel {
    partnersId: number;
    selectedLicensee: string;
    partnerLincenseeInformationId: number;
    stateId: number;
    licenseState: string;
    nameOfForm: string;
    dueDate: string;
    isWetSignature: boolean;
    isElectronicSignature: boolean;
    isDigitalSignature: boolean;
    notarizationRequired: boolean;
    files: File[];
}
export const InitNewRequestSignatureModel: AddRequestSignatureModel = {
    partnersId: 0,
    selectedLicensee: '',
    partnerLincenseeInformationId: 0,
    stateId: 0,
    licenseState: '',
    nameOfForm: '',
    dueDate: '',
    isWetSignature: false,
    isElectronicSignature: false,
    isDigitalSignature: false,
    notarizationRequired: undefined as unknown as boolean,
    files: [],
};

interface Iprops {
    Open: boolean;
    handleClose: (isReload: boolean) => void;
    partners: PartnerModel[];
    licenseeRows: PartnerLicenseeRow[];
    states: StatesModel[];
    loadingLicenseeInfo?: boolean;
    onClientChange?: (partnersId: number) => void;
}

const sectionTitleSx = { color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 };
const paperSectionSx = { p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' };
const selectFieldSx = {
    width: '100%',
    '& .MuiSelect-select': { padding: '14.5px 16px' },
    '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};
const textFieldSx = {
    '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
    '& .MuiInputBase-input': { padding: '5px 14px !important' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};

const AddRequestSignatureValidationSchema = yup.object({
    partnersId: yup.number().min(1, 'Please select a Client').required('Client is required'),
    selectedLicensee: yup.string().trim().required('Please select a Signer (Licensee) after selecting a Client'),
    partnerLincenseeInformationId: yup.number().min(1, 'Please select a Signer (Licensee)').required('Signer is required'),
    stateId: yup.number().min(1, 'Please select a State').required('State is required'),
    nameOfForm: yup.string().trim().required('Name of Form is required'),
    dueDate: yup.string().required('Due Date is required'),
    isWetSignature: yup.boolean(),
    isElectronicSignature: yup.boolean(),
    isDigitalSignature: yup.boolean(),
    notarizationRequired: yup.boolean().oneOf([true, false], 'Please select Yes or No').required('Notarization Option is required'),
});

export const AddRequestSignature: React.FC<Iprops> = ({ Open, handleClose, partners, licenseeRows, states, loadingLicenseeInfo = false, onClientChange }) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);

    const distinctLicensees = useMemo(() => {
        const set = new Set<string>();
        (licenseeRows || []).forEach((r) => set.add(r.licensee));
        return Array.from(set).sort();
    }, [licenseeRows]);

    const stateOptions = useMemo(
        () => (states || []).filter((s) => s.stateId > 0).sort((a, b) => a.stateName.localeCompare(b.stateName)),
        [states],
    );

    const FMSelectPartnersCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);
        const { setFieldValue, setFieldTouched } = useFormikContext<AddRequestSignatureModel>();
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                {/* <FormHelperText className='formtexthelper-heading'>Select Client</FormHelperText> */}
                <Autocomplete
                    options={partners}
                    value={partners.find((item) => Number(item.partnersId) === Number(field.value)) ?? null}
                    getOptionLabel={(option: PartnerModel) => option.customerName}
                    onBlur={() => helpers.setTouched(true)}
                    sx={{
                        '& .MuiFormLabel-root': { transform: 'translate(1px, -32px) scale(1)', fontSize: '16px !important', color: '#212529 !important', fontWeight: '400 !important' },
                        '& .MuiInputBase-root': { padding: '10px 9px' },
                        '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px', border: '2px solid #e2e8f0', padding: '12px 16px'},
                        paddingTop: '38px'
                    }}
                    onChange={(e, value) => {
                        const partnersId = value?.partnersId != null ? Number(value.partnersId) : 0;
                        helpers.setValue(partnersId);
                        setFieldValue('selectedLicensee', '');
                        setFieldValue('partnerLincenseeInformationId', 0);
                        setFieldValue('stateId', 0);
                        setFieldValue('licenseState', '');
                        setFieldTouched('selectedLicensee', false);
                        setFieldTouched('stateId', false);
                        if (onClientChange) {
                            onClientChange(partnersId);
                        }
                    }}
                    renderInput={(params: AutocompleteRenderInputParams) => <TextField {...params} sx={{ '& .MuiInputBase-input': { height: '30px !important', paddingLeft: '5px', marginTop: '-3px !important' }, '& .MuiOutlinedInput-notchedOutline legend' : { display: 'none'} }} label="Select Client" />}
                />
                {/* <Select {...field}
                    labelId="Partner-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                    onChange={(e) => {
                        const partnersId = e.target.value as number;
                        console.log('e', e.target.value);
                        helpers.setValue(partnersId);
                        setFieldValue('selectedLicensee', '');
                        setFieldValue('partnerLincenseeInformationId', 0);
                        setFieldValue('stateId', 0);
                        setFieldValue('licenseState', '');
                        if (onClientChange && partnersId > 0) {
                            onClientChange(partnersId);
                        }
                    }}
                >
                    {partners.map((partner, key) =>
                        <MenuItem key={key} value={partner.partnersId}>{partner.customerName}</MenuItem>
                    )}
                </Select> */}
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };
    const FMSelectLicensee: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta] = useField<{}>(props);
        const { values, setFieldValue, setFieldTouched } = useFormikContext<AddRequestSignatureModel>();
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <FormHelperText className='formtexthelper-heading'>Select Signer (Licensee)</FormHelperText>
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <Select {...field}
                        labelId="Licensee-Select-Label"
                        IconComponent={loadingLicenseeInfo ? () => null : ExpandMoreIcon}
                        label={null}
                        disabled={values.partnersId === 0 || loadingLicenseeInfo}
                        displayEmpty
                        renderValue={(v: unknown) => (v as string) || 'Select a Signer'}
                        sx={{
                            ...selectFieldSx,
                            marginTop: '5px',
                            '& .MuiSelect-icon': { display: loadingLicenseeInfo ? 'none' : 'block' },
                        }}
                        onChange={(e) => {
                            const name = e.target.value as string;
                            const firstRow = (licenseeRows || []).find((r) => r.licensee === name);
                            setFieldValue('partnerLincenseeInformationId', firstRow?.partnerLincenseeInformationId ?? 0);
                            setFieldValue('stateId', 0);
                            setFieldValue('licenseState', '');
                            setFieldTouched('stateId', false);
                            setFieldValue('selectedLicensee', name, true);
                        }}
                    >
                        <MenuItem value="" disabled>Select a Client first</MenuItem>
                        {distinctLicensees.map((licensee, key) => (
                            <MenuItem key={key} value={licensee}>{licensee}</MenuItem>
                        ))}
                    </Select>
                    {loadingLicenseeInfo && (
                        <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', right: 14, marginTop: '-10px', pointerEvents: 'none' }} />
                    )}
                </Box>
                {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
            </FormControl>
        );
    };
    const FMSelectRequestState: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);
        const { values, setFieldValue } = useFormikContext<AddRequestSignatureModel>();
        const stateSelectDisabled =
            values.partnersId === 0 ||
            !values.selectedLicensee ||
            values.partnerLincenseeInformationId === 0 ||
            loadingLicenseeInfo ||
            stateOptions.length === 0;
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                {/* <Autocomplete
                    options={filteredRows}
                    getOptionLabel={(option: PartnerLicenseeRow) => option.stateName}
                    value={filteredRows.find((item) => Number(item.stateId) === Number(field.value)) ?? null}
                    sx={{
                        '& .MuiFormLabel-root': { transform: 'translate(1px, -32px) scale(1)', fontSize: '16px !important', color: '#212529 !important', fontWeight: '400 !important' },
                        '& .MuiInputBase-root': { padding: '10px 9px' },
                        '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px', border: '2px solid #e2e8f0', padding: '12px 16px'},
                        paddingTop: '38px'
                    }}
                    onChange={(e, value) => {
                        const stateId = value?.stateId != null ? Number(value.stateId) : 0;
                        helpers.setValue(stateId);
                    }}
                    onBlur={() => helpers.setTouched(true)}
                    renderInput={(params: AutocompleteRenderInputParams) => <TextField {...params} sx={{ '& .MuiInputBase-input': { height: '30px !important' }, '& .MuiOutlinedInput-notchedOutline legend' : { display: 'none'} }} label="Select State" />}
                /> */}
                <FormHelperText className='formtexthelper-heading'>Select State</FormHelperText>
                <Select {...field}
                    labelId="State-Select-Label"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                    disabled={stateSelectDisabled}
                    displayEmpty
                    sx={selectFieldSx}
                    renderValue={(v) => {
                        const sid = v as number;
                        const opt = stateOptions.find((s) => s.stateId === sid);
                        return opt ? opt.stateName : 'Select a State';
                    }}
                    onChange={(e) => {
                        const id = Number(e.target.value);
                        const opt = stateOptions.find((s) => s.stateId === id);
                        setFieldValue('licenseState', opt?.postalAbbv ?? '');
                        setFieldValue('stateId', id, true);
                    }}
                >
                    <MenuItem value={0} disabled>Select a Signer first</MenuItem>
                    {stateOptions.map((s) => (
                        <MenuItem key={s.stateId} value={s.stateId}>
                            {s.stateName}
                        </MenuItem>
                    ))}
                </Select>
                {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
            </FormControl>
        );
    };
    const FMSelectNotarization: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta] = useField<{}>(props);
        const { setFieldValue } = useFormikContext<AddRequestSignatureModel>();
        return (
            <FormControl error={meta.touched && !!meta.error}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.7 }}>
                    <FormHelperText className='formtexthelper-heading' sx={{ flexShrink: 0, margin: 0 }}>Notarization Required:</FormHelperText>
                    <Select name={field.name}
                        value={field.value === true ? 'true' : field.value === false ? 'false' : ''}
                        displayEmpty
                        renderValue={(v) => { if (v === 'true') return 'Yes'; if (v === 'false') return 'No'; return 'Select Yes or No'; }}
                        onChange={(e) => {
                            const val = e.target.value as string;
                            setFieldValue('notarizationRequired', val === '' ? undefined : val === 'true');
                        }}
                        onBlur={field.onBlur}
                        labelId="Notarization-Select-Label"
                        IconComponent={ExpandMoreIcon}
                        label={null}
                        sx={{ ...selectFieldSx, '& .MuiSelect-icon': { display: loadingLicenseeInfo ? 'none' : 'block' } }}
                    >
                        <MenuItem value="" disabled>Select Yes or No</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                    </Select>
                </Box>
                {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
            </FormControl>
        );
    };

    const onCloseDialog = () => handleClose(false);

    return (
        <Dialog
            open={Open}
            onClose={() => handleClose(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    width: '90%',
                    maxWidth: 900,
                    minHeight: '50vh',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                },
            }}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
        >
            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DrawOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                    <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                        Create New Request for Signature
                    </Typography>
                </Box>
                <IconButton
                    onClick={onCloseDialog}
                    sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
                    aria-label="Close dialog"
                >
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <Formik
                initialValues={InitNewRequestSignatureModel}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnMount={true}
                validationSchema={AddRequestSignatureValidationSchema}
                validate={(values) => {
                    const errors: Record<string, string> = {};
                    if (!values.isWetSignature && !values.isElectronicSignature && !values.isDigitalSignature) {
                        errors.signatureType = 'At least one signature type (Wet, Electronic, or Digital) must be selected';
                    }
                    return errors;
                }}
                onSubmit={(data, { setSubmitting }) => {
                    const clientName = partners.find((p) => p.partnersId === data.partnersId)?.customerName || 'the selected partner';
                    Swal.fire({
                        title: '<div style="color: white;">Are you sure?</div>',
                        html: `<div style="color: white;">This will send a signature request to the Client Portal for <span style="color: yellow;">${clientName}</span></div>`,
                        showCancelButton: true,
                        confirmButtonText: 'Yes, Proceed',
                        cancelButtonText: 'No, Continue to EDIT',
                        confirmButtonColor: GreenBtnColor,
                        cancelButtonColor: OrangeBtnColor,
                        reverseButtons: true,
                        background: PrimaryBlueColor,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            setSubmitting(true);
                            setLoading(true);
                            const formData = new FormData();
                            formData.append("data", JSON.stringify({
                                partnersId: data.partnersId,
                                partnerLincenseesInformationId: data.partnerLincenseeInformationId,
                                licenseState: data.licenseState,
                                stateId: data.stateId,
                                nameOfForm: data.nameOfForm,
                                dueDate: data.dueDate,
                                isWetSignature: data.isWetSignature,
                                isElectronicSignature: data.isElectronicSignature,
                                isDigitalSignature: data.isDigitalSignature,
                                notarizationRequired: data.notarizationRequired,
                            }));
                            if (data.files?.length) {
                                data.files.forEach((file) => formData.append("files", file));
                            }
                            apiClient('/SignatureRequests/addSignatureRequest', {
                                method: 'POST',
                                headers: new Headers({ 'Authorization': `Bearer ${token}` }),
                                body: formData,
                            }).then((response) => {
                                if (!response) return;
                                if (response.status) {
                                    Swal.fire({ title: 'Success', text: response.message || 'The signature request has been sent to the client.', icon: 'success' });
                                    handleClose(true);
                                } else {
                                    throw new Error(response.message || 'Failed to send signature request to client.');
                                }
                            }).catch((error) => {
                                Swal.fire({ title: 'Error', text: error.message || 'Failed to send signature request to client.', icon: 'error' });
                            }).finally(() => {
                                setSubmitting(false);
                                setLoading(false);
                            });
                        } else {
                            setSubmitting(false);
                            setLoading(false);
                        }
                    });
                }}
            >
                {({ values, setFieldValue, setFieldTouched, errors, touched, validateForm, isValid, isSubmitting }) => {
                    const todayString = new Date().toISOString().split('T')[0];
                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { name, type } = e.target;
                        const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
                        setFieldValue(name, value);
                        setFieldTouched(name, true);
                        if (['isWetSignature', 'isElectronicSignature', 'isDigitalSignature'].includes(name)) {
                            setTimeout(() => validateForm(), 0);
                        }
                    };
                    const signatureTypeError = (touched.isWetSignature || touched.isElectronicSignature || touched.isDigitalSignature) ? (errors as any).signatureType : undefined;
                    return (
                        <Form className='main-form'>
                            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3, overflow: 'auto', maxHeight: '70vh' }}>
                                <Grid container spacing={3}>
                                    {/* Section 1: Client, signer & notarization */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={sectionTitleSx}>Client, signer & notarization</Typography>
                                        <Paper variant="outlined" sx={paperSectionSx}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <FMSelectPartnersCustom name="partnersId" />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FMSelectLicensee name="selectedLicensee" />
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>

                                    {/* Section 2: Form details */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={sectionTitleSx}>Form details</Typography>
                                        <Paper variant="outlined" sx={paperSectionSx}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={4}>
                                                    <FMSelectRequestState name="stateId" />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <FormControl fullWidth>
                                                        <FormHelperText className="formtexthelper-heading">Type Name of Form</FormHelperText>
                                                        <FMTextField name="nameOfForm" value={values.nameOfForm} onChange={handleChange} label={null} type="text" fullWidth variant="outlined" sx={{ ...textFieldSx, width: '100%' }} />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <FormControl fullWidth>
                                                        <FormHelperText className="formtexthelper-heading">Select Due Date</FormHelperText>
                                                        <FMTextField name="dueDate" value={values.dueDate} onChange={handleChange} label={null} type="date" fullWidth variant="outlined" sx={{ ...textFieldSx, width: '100%' }} inputProps={{ min: todayString }} />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>

                                    {/* Section 3: Signature type */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={sectionTitleSx}>Signature type</Typography>
                                        <Paper variant="outlined" sx={paperSectionSx}>
                                            <FormControl component="fieldset" error={!!signatureTypeError} fullWidth>
                                                <FormHelperText className="formtexthelper-heading">Signature Type(s)</FormHelperText>
                                                <RadioGroup row sx={{ pt: 0.5 }}>
                                                    <FormControlLabel control={<Checkbox name="isWetSignature" checked={values.isWetSignature} onChange={handleChange} />} label="Wet Signature" />
                                                    <FormControlLabel control={<Checkbox name="isElectronicSignature" checked={values.isElectronicSignature} onChange={handleChange} />} label="Electronic" />
                                                    <FormControlLabel control={<Checkbox name="isDigitalSignature" checked={values.isDigitalSignature} onChange={handleChange} />} label="Digital" />
                                                </RadioGroup>
                                                {signatureTypeError && <FormHelperText error>{signatureTypeError}</FormHelperText>}
                                            </FormControl>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FMSelectNotarization name="notarizationRequired" />
                                    </Grid>

                                    {/* Section 4: Attachments */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={sectionTitleSx}>Attachments</Typography>
                                        
                                            <FormControl sx={{ width: '100%' }}>
                                                <FormHelperText className="formtexthelper-heading">Select File(s) or Drag and Drop</FormHelperText>
                                                <StyledDropzone
                                                    label="Select File(s) or Drag and Drop"
                                                    onFileDrop={(files: File[]) => setFieldValue("files", [...values.files, ...files])}
                                                />
                                                {values.files && values.files.length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <FormHelperText sx={{ mb: 1 }}>Selected Files ({values.files.length}):</FormHelperText>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            {values.files.map((file, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={file.name}
                                                                    onDelete={() => setFieldValue("files", values.files.filter((_, i) => i !== index))}
                                                                    deleteIcon={<CancelIcon />}
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </FormControl>
                                        
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, py: 2, gap: 1.5, borderTop: 1, borderColor: 'divider', backgroundColor: 'grey.50', flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={onCloseDialog}
                                    startIcon={<CloseOutlinedIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#2099d2', backgroundColor: '#2099d214', color: '#2099d2' } }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="medium"
                                    disabled={!isValid || isSubmitting}
                                    startIcon={<SendOutlinedIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#2099d2', '&:hover': { backgroundColor: '#176990' }, px: 2.5 }}
                                >
                                    Send to Client
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};